import pluralize from 'pluralize'
import { useEffect, useState } from 'react'

import { Button, ProgressBar } from '@/components/ui'
import { DISABLE_PROCESSING } from '@/config'
import {
  FileDropzone,
  type FileDropzoneItem,
  type PresignedBlob,
  useUpload,
} from '@/modules/upload'
import { formatBytes } from '@/utils'

interface UploadFormProps {
  onCancel?: () => void
  onSubmit?: (blobs: PresignedBlob[]) => void
  // Lets a caller that can hide this form warn about files staged here but never sent.
  onStagedFilesChange?: (count: number) => void
  maxSize?: number
  disabled?: boolean
  submitText?: string
  collapsed?: boolean
  pageWideDrop?: boolean
}

export const UploadForm = ({
  onCancel,
  onSubmit,
  onStagedFilesChange,
  disabled,
  maxSize,
  submitText,
  collapsed = false,
  pageWideDrop = false,
}: UploadFormProps) => {
  const { upload, progress, errorsByFile, resetProgress } = useUpload()
  const [submitted, setSubmitted] = useState(false)
  const [files, setFiles] = useState<FileDropzoneItem[]>([])
  const [error, setError] = useState<string | null>(null)
  const hasFilesWithError = files.some((file) => 'errors' in file)

  const reset = () => {
    setTimeout(() => {
      resetProgress()
      setFiles([])
      setError(null)
      setSubmitted(false)
    }, 300)
  }

  const handleError = (message: string) => {
    setError(message)
    setSubmitted(false)
    resetProgress({ skipErrors: true })
  }

  const handleCancel = () => {
    onCancel?.()
    reset()
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setSubmitted(true)
    setError(null)
    if (!files.length) {
      onSubmit?.([])
      reset()
      return
    }

    upload(files as File[])
      .then((blobs) => onSubmit?.(blobs))
      .then(() => reset())
      .catch((e) => handleError(e.message))
  }

  useEffect(() => {
    onStagedFilesChange?.(files.length)
  }, [files])

  useEffect(() => {
    const uploadErrors = errorsByFile.filter((error) => error)

    if (uploadErrors.length > 0) {
      handleError(
        `Failed to upload ${uploadErrors.length} files. Please try again or remove the files.`,
      )
    }
    if (hasFilesWithError) {
      setError(
        `Some files exceed the ${maxSize}MB limit. Please remove them to proceed.`,
      )
    }

    if (!hasFilesWithError && !uploadErrors.length && error) {
      setError(null)
    }
  }, [errorsByFile, hasFilesWithError])
  const showButtons = !collapsed || files.length > 0
  const submitLabel =
    submitText ??
    (files.length > 0
      ? `Upload ${files.length} ${pluralize('file', files.length)}`
      : 'Upload')

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="relative">
        <div className={progress.percent > 0 ? 'invisible' : undefined}>
          <FileDropzone
            accept={{ 'application/pdf': ['.pdf'] }}
            maxSize={maxSize}
            disabled={DISABLE_PROCESSING || disabled}
            setFiles={setFiles}
            files={files}
            errorsByFile={errorsByFile}
            collapsed={collapsed}
            pageWideDrop={pageWideDrop}
          />
        </div>
        {progress.percent > 0 && (
          <div className="absolute inset-0 flex flex-col justify-center gap-2 rounded-md border bg-blue-1 px-4">
            <p className="inline-flex items-center gap-1 text-sm">
              Uploading...
            </p>
            <ProgressBar progress={progress.percent} />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>
                {formatBytes(progress.loaded)} / {formatBytes(progress.total)}
              </span>
              <span>{progress.percent.toFixed(2)}%</span>
            </div>
          </div>
        )}
      </div>
      {error && <p className="mb-1 text-sm text-red-600">{error}</p>}
      {showButtons && (
        <div className="flex justify-end gap-2">
          {onCancel && (
            <Button theme="outline" onClick={handleCancel}>
              Cancel
            </Button>
          )}
          <Button
            type="submit"
            isLoading={progress.percent > 0}
            disabled={
              DISABLE_PROCESSING ||
              submitted ||
              progress.percent > 0 ||
              files.length === 0 ||
              hasFilesWithError ||
              disabled
            }
          >
            {submitLabel}
          </Button>
        </div>
      )}
    </form>
  )
}
